import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { ChipModule } from 'primeng/chip';
import { ChipsModule } from 'primeng/chips';
import { IconFieldModule } from 'primeng/iconfield';
import { InputTextModule } from 'primeng/inputtext';
import { InputIconModule } from 'primeng/inputicon';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox'
import { ButtonModule } from 'primeng/button';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { InputSwitchModule } from 'primeng/inputswitch';
import { CalendarModule } from 'primeng/calendar';

import { ReviewerReponsePayload } from '../interfaces/reports.interface';
import { BaseReport } from '../base-class/base-report.class';
import { ReportService } from '../reports.service';
import { StorageService } from '../../../../../shared/services/storage.service';
import { DateTimeService } from '../../../../../shared/services/date-time.service';
import { UserService } from '../../../../../shared/services/user.service';
import { HelperService } from '../../../../../shared/services/helper.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import moment from 'moment';
import { saveAs } from 'file-saver';
import * as pdfMake from 'pdfmake/build/pdfmake';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { PipesModule } from '../../../../../shared/pipes/pipes.module';

@Component({
  selector: 'app-all-working-reviewers',
  standalone: true,
  imports: [InputSwitchModule,CalendarModule,CheckboxModule,OverlayPanelModule,FloatLabelModule,MultiSelectModule,DropdownModule, ButtonModule, FormsModule, ChipModule, ChipsModule, IconFieldModule,InputIconModule,InputTextModule,TableModule, CommonModule, PipesModule ],
  templateUrl: './all-working-reviewers.component.html',
  styleUrl: './all-working-reviewers.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AllWorkingReviewersComponent extends BaseReport implements OnInit {
  showProjectSelectionError = false;
  includePendingReviewers = false;
  reviewersList: Array<ReviewerReponsePayload> = [];
  shareReport = false;
  emails: Array<string> = [];

  constructor(
    override reportService: ReportService,
    override storageService: StorageService,
    override dateTimeService: DateTimeService,
    override userService: UserService,
    public helperService: HelperService,
    private toastrService: ToastService
  ){
    super(reportService, storageService, dateTimeService, userService);
  }

  ngOnInit() {
    this.initializeReport();
    this.emails.push(this.sessionUser.email);
  }

  override async onProjectSelectionChange(e: any): Promise<void>{
    await super.onProjectSelectionChange(e);
    this.showProjectSelectionError = false;
  }

  getReviewers(): void{
    if(!this.selectedProjects.length){
      this.showProjectSelectionError = true;
      return;
    }
    const reviewers: Array<ReviewerReponsePayload> = [];

    const filters = {
      firmId: this.userService.getUserFirmId(),
      includePendingReviewers: this.includePendingReviewers,
      isPresumptiveEnabled: this.sessionUser.firm.isPresumptiveEnabled,
      isSentEmail: false,
      project: this.selectedProjects.map(project => project._id),
      role: this.selectedRoles.map(role => role.id),
      selectedProj: this.selectedProjects
    };

    this.reportService.generateReviewersReport(filters).subscribe({
      next: (reviewerResponse: Array<any>) => {
        reviewerResponse.forEach(reviewer => {
          reviewers.push({
            _id: reviewer.reviewer._id,
            firstName: reviewer.reviewer.firstName,
            lastName: reviewer.reviewer.lastName,
            email: reviewer.reviewer.email,
            phone: reviewer.reviewer.profile.contact.home,
            project: reviewer.project.codeName,
            role: reviewer.role,
            firm: reviewer.firm,
            positionStartDate: reviewer.positionStartDate,
            presumptive: reviewer.presumptive
          });
        });
        this.reviewersList = [...reviewers];
      }
    });
  };

  displayShareReportOption(): void{
    this.shareReport = !this.shareReport;
  }

  checkEmailListsAreValid(): boolean{
    for(const email of this.emails){
      if(!this.helperService.isEmail(email)){
        return false;
      }
    }
    return true;
  }

  async sendMail(): Promise<void>{
    if(this.emails.length && this.checkEmailListsAreValid()){
      const filters = {
        emails: this.emails.join(','),
        firmId: this.userService.getUserFirmId(),
        includePendingReviewers: this.includePendingReviewers,
        isPresumptiveEnabled: this.sessionUser.firm.isPresumptiveEnabled,
        isSentEmail: true,
        project: this.selectedProjects.map(project => project._id),
        role: this.selectedRoles.map(role => role.id),
        selectedProj: this.selectedProjects
      };

      this.reportService.generateReviewersReport(filters).subscribe({
        next: () => {
          this.toastrService.showSuccess('Active reviewers report shared successfully');
        },
        error: (error) => {
          if (error && error.message && typeof error.message === 'string') {
            this.toastrService.showError(error.message);
          } else {
              this.toastrService.showError((error.message || 'Some thing went wrong').join(','));
          }
        }
      });
      
    }
  }

  onExportCSV() {
    const fileData = [
        'First Name,Last Name,Email,Primary Phone,Firm,Project, Role,Start Date \r\n'
    ];
    const {activeReviewers, pendingReviewers} = this.getActiveAndPendingReviewersByStatus();
    if (activeReviewers.length || pendingReviewers.length) {
        activeReviewers.forEach((r) => {
            const data = [
                r.firstName,
                r.lastName,
                r.email,
                r.phone,
                r.firm.name,
                r.project,
                r.role.roleType,
                r.positionStartDate || r.role.startAt ? this.formatDate(r.positionStartDate || r.role.startAt) : 'Start date not finalized'
            ].join(',') + '\r\n';
            fileData.push(data);
        });

        if (pendingReviewers.length) {
            let data = [
                '',
                '',
                '',
                '',
                '',
                ''
            ].join(',') + '\r\n';
            fileData.push(data);

            data = [
                'PENDING REVIEWERS',
                '',
                '',
                '',
                '',
                ''
            ].join(',') + '\r\n';
            fileData.push(data);

            data = [
                '',
                '',
                '',
                '',
                '',
                ''
            ].join(',') + '\r\n';
            fileData.push(data);
        }

        pendingReviewers.forEach((r) => {
            const data = [
                r.firstName,
                r.lastName,
                r.email,
                r.phone,
                r.firm.name,
                r.project,
                r.role.roleType,
                r.positionStartDate || r.role.startAt ? this.formatDate(r.positionStartDate || r.role.startAt) : 'Start date not finalized'
            ].join(',') + '\r\n';
            fileData.push(data);
        });

        const blob = new Blob(fileData, { type: "text/plain;charset=utf-8" });
        saveAs(blob, 'active-working-reviewer.csv');
    }
    else {
        this.toastrService.showError('No Active Reviewer on Any Project.');
    }
  }

  async onExportPDF() {
    const {activeReviewers, pendingReviewers} = this.getActiveAndPendingReviewersByStatus();
    const rows = [];
    const headerOptions = {
        bold: true,
        width: '12.5%',
        alignment: 'justify'
    };
    const headers = [
        this.generateColumn('First Name', headerOptions),
        this.generateColumn('Last Name', headerOptions),
        this.generateColumn('Email', headerOptions),
        this.generateColumn('Primary Phone', headerOptions),
        this.generateColumn('Firm', headerOptions),
        this.generateColumn('Project', headerOptions),
        this.generateColumn('Role', headerOptions),
        this.generateColumn('Start Date', headerOptions)
    ];
    rows.push(headers);
   
    activeReviewers.forEach((r: any) => {
        const cols = [
            this.generateColumn(r.firstName),
            this.generateColumn(r.lastName),
            this.generateColumn(r.email),
            this.generateColumn(r.phone),
            this.generateColumn(r.firm.name),
            this.generateColumn(r.project),
            this.generateColumn(r.role.roleType),
            this.generateColumn(
                r.positionStartDate || r.role.startAt ? this.formatDate(r.positionStartDate || r.role.startAt) : 'Start date not finalized'
            )
        ];
        rows.push(cols);
    });

    const tableContent: Array<any> = [
        {
            image: await this.getImageAsBase64(this.helperService.getLogo('report')),
            width: this.helperService.getEnvironmentName() === 'pwc' ? 650 : 140,
            height: 65,
            alignment: 'left',
            margin: [0, 0, 0, 0]
        },
        {
            text: ' ',
            style: 'header'
        },
        {
            text: ' ',
            style: 'header'
        },
        {
            text: 'Active Working Reviewers Report',
            style: 'header',
            bold: true,
            fontSize: 22,
            margin: [0, 20, 0, 20]
        },
        {
            layout: 'lightHorizontalLines',
            table: {
                headerRows: 1,
                body: rows,
                widths: [83,83,83,83,83,83,83,83]
            }
        }
    ];

    let header2: Array<any> = [];
    const row2 = [];

    if (pendingReviewers.length) {
        header2 = [{
            text: "Pending Reviewers",
            width: '*',
            rowSpan: 1,
            alignment: 'center',
            fontSize: 14,
            bold: true,
            colSpan: 8,
            fillColor: '#bdbdbd',
            color: 'black',
            margin: [0, 10, 0, 10]
        }, {}, {}, {}, {}, {}, {}, {}];
        row2.push(header2);

        pendingReviewers.forEach((r: any) => {
            const cols = [
                this.generateColumn(r.firstName),
                this.generateColumn(r.lastName),
                this.generateColumn(r.email),
                this.generateColumn(r.phone),
                this.generateColumn(r.firm.name),
                this.generateColumn(r.project),
                this.generateColumn(r.role.roleType),
                this.generateColumn(
                    r.positionStartDate || r.role.startAt ? this.formatDate(r.positionStartDate || r.role.startAt) : 'Start date not finalized'
                )
            ];
            row2.push(cols);
        });

        tableContent.push(
            {
                layout: 'lightHorizontalLines',
                table: {
                    headerRows: 0,
                    body: row2,
                    widths: [83,83,83,83,83,83,83,83]
                }
            });
    }

    if (activeReviewers.length || pendingReviewers.length) {
        const docDefinition: TDocumentDefinitions = {
            pageOrientation: 'landscape',
            content: tableContent
        };
        pdfMake.createPdf(docDefinition).download('active-working-reviewer');
    }
    else {
        this.toastrService.showInfo('No record found');
    }
  }

  formatDate(date: string): string {
    return moment(date).format('MM/dd/yyyy');
  }

  getActiveAndPendingReviewersByStatus(): {
    activeReviewers: Array<ReviewerReponsePayload>,
    pendingReviewers: Array<ReviewerReponsePayload>
  }{
    const activeAndPendingRevewer: {
      activeReviewers: Array<ReviewerReponsePayload>,
      pendingReviewers: Array<ReviewerReponsePayload>
    } = {
      activeReviewers: [],
      pendingReviewers: []
    }
    this.reviewersList.forEach(reviewer => {
      if (reviewer.presumptive && reviewer.presumptive.status === 'ASSIGNED') {
        activeAndPendingRevewer.pendingReviewers.push(reviewer);
      } else {
          activeAndPendingRevewer.activeReviewers.push(reviewer);
      }
    });

    return activeAndPendingRevewer;
  }

  async getImageAsBase64(imagePath: string): Promise<string> {
    const res = await fetch(imagePath);
    const blob = await res.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);

      reader.readAsDataURL(blob);
    });
  }
}